/**
 * /mentions-legales — Mentions légales SmartLawyer
 */

import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { PublicHeader, PublicFooter } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';

const SITE_URL = 'https://smartlawyer.ai';

export function MentionsLegales() {
  const { t } = useTranslation();
  const strong = <strong className="text-foreground" />;
  return (
    <>
      <Helmet>
        <title>{t('legal.notice.meta_title')}</title>
        <meta name="description" content={t('legal.notice.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/mentions-legales`} />
        <meta name="robots" content="noindex, follow" />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />

        <Breadcrumb items={[
          { label: t('menu.home'),        to: '/' },
          { label: t('legal.notice.title') },
        ]} />

        <main className="flex-1 max-w-3xl mx-auto w-full px-4 sm:px-6 py-12">
          <h1 className="text-3xl font-bold mb-2">{t('legal.notice.title')}</h1>
          <p className="text-sm text-muted-foreground mb-10">
            {t('legal.notice.intro')}
          </p>

          <div className="space-y-10 text-sm leading-relaxed">

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.publisher.title')}</h2>
              <p className="text-muted-foreground">
                <Trans
                  i18nKey="legal.notice.publisher.text"
                  components={{
                    site: <a href="https://smartlawyer.ai" className="text-primary hover:underline" />,
                    strong,
                  }}
                />
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.director.title')}</h2>
              <p className="text-muted-foreground">
                <Trans i18nKey="legal.notice.director.text" components={{ strong }} />
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.host.title')}</h2>
              <p className="text-muted-foreground">
                <Trans i18nKey="legal.notice.host.p1" components={{ strong }} />
              </p>
              <p className="text-muted-foreground mt-2">
                <Trans i18nKey="legal.notice.host.p2" components={{ strong }} />
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.ip.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.notice.ip.p1')}
              </p>
              <p className="text-muted-foreground mt-2">
                {t('legal.notice.ip.p2')}
              </p>
              <p className="text-muted-foreground mt-2">
                {t('legal.notice.ip.p3')}
              </p>
              <p className="text-muted-foreground mt-2">
                <Trans
                  i18nKey="legal.notice.ip.p4"
                  components={{ addr: <span className="text-foreground" /> }}
                />
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.gdpr.title')}</h2>
              <p className="text-muted-foreground">
                {t('legal.notice.gdpr.p1')}
              </p>
              <p className="text-muted-foreground mt-2">
                {t('legal.notice.gdpr.p2')}
              </p>
              <p className="text-muted-foreground mt-2">
                {t('legal.notice.gdpr.p3')}{' '}
                <a href="mailto:rgpd@smartlawyer.ai" className="text-primary hover:underline">
                  rgpd@smartlawyer.ai
                </a>
              </p>
            </section>

            <section>
              <h2 className="text-base font-semibold mb-3">{t('legal.notice.contact.title')}</h2>
              <div className="text-muted-foreground space-y-1">
                <p>
                  {t('legal.notice.contact.phone')}{' '}
                  <a href="tel:+33650710560" className="text-primary hover:underline">06 50 71 05 60</a>
                </p>
                <p>
                  {t('legal.notice.contact.email')}{' '}
                  <a href="mailto:contact@smartlawyer.ai" className="text-primary hover:underline">
                    contact@smartlawyer.ai
                  </a>
                </p>
              </div>
            </section>

          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
